import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  getBuildingSelectProps,
  needsBuildingSelection,
  persistSelectedBuilding,
  selectBuilding,
  shouldShowBuildingSelector,
} from '../utils/buildingSelection';
import { paymentApi } from '../api/paymentApi';
import { contractsApi } from '../api/contractsApi';
import { buildingsApi } from '../api/buildingsApi';
import ApartmentServiceFeePaymentModal from '../components/modals/ApartmentServiceFeePaymentModal';
import toast from 'react-hot-toast';

interface Building {
  id: string;
  name: string;
}

interface Apartment {
  id: string;
  code: string;
  ownerName: string;
  ownerPhone?: string;
  building: Building;
}

export const Payment: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('contractId');
  const buildingIdFromQuery = searchParams.get('buildingId');
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (contractId && selectedBuildingId) {
      loadContractPaymentContext();
    }
  }, [contractId, selectedBuildingId]);

  const loadContractPaymentContext = async () => {
    try {
      const context = await contractsApi.getPaymentContext(contractId!);
      const firstApartment = context.apartments?.[0];
      if (firstApartment?.code) {
        setSearchValue(firstApartment.code);
        const found = await paymentApi.searchApartment(selectedBuildingId, firstApartment.code);
        if (found) {
          setApartment(found);
          setPaymentKey((key) => key + 1);
        }
      }
    } catch {
      toast.error('Không thể tải thông tin hợp đồng để thanh toán');
    }
  };

  const fetchBuildings = async () => {
    try {
      const data = await buildingsApi.getBuildings();
      setBuildings(data);
      const preferredBuildingId = buildingIdFromQuery || '';
      const nextId = applyDefaultBuildingSelection(
        data,
        user,
        preferredBuildingId || '',
      );
      if (preferredBuildingId) {
        persistSelectedBuilding(user?.id, preferredBuildingId);
      } else if (nextId) {
        persistSelectedBuilding(user?.id, nextId);
      }
      setSelectedBuildingId(nextId);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      toast.error('Không tải được danh sách chung cư');
    }
  };

  const handleSearch = async () => {
    if (!selectedBuildingId || !searchValue.trim()) {
      toast.error('Vui lòng chọn chung cư và nhập mã căn hộ, tên căn hộ hoặc số điện thoại');
      return;
    }

    setIsSearching(true);
    try {
      const found = await paymentApi.searchApartment(selectedBuildingId, searchValue.trim());
      if (found) {
        setApartment(found);
        setPaymentKey((key) => key + 1);
      } else {
        setApartment(null);
        toast.error('Không tìm thấy căn hộ');
      }
    } catch (error) {
      console.error('Error searching apartment:', error);
      toast.error('Lỗi khi tìm kiếm căn hộ');
      setApartment(null);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">PHÍ DỊCH VỤ</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">Thanh toán</span>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Thanh toán phí dịch vụ</h1>
        <p className="text-sm text-gray-600">
          Tìm căn hộ, thêm hóa đơn nhanh và thu phí trên cùng một màn hình.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {shouldShowBuildingSelector(buildings) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chung cư</label>
              <select
                value={selectedBuildingId}
                onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="">Chọn chung cư</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              MÃ CĂN HỘ, TÊN CĂN HỘ HOẶC SỐ ĐIỆN THOẠI
            </label>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="NHẬP MÃ CĂN HỘ, TÊN CĂN HỘ HOẶC SỐ ĐIỆN THOẠI"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? 'Đang tìm...' : 'TÌM KIẾM'}
            </button>
          </div>
        </div>
      </div>

      {apartment && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Thông tin căn hộ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-gray-600">Mã căn hộ: </span>
              <button
                onClick={() => navigate(`/apartments/${apartment.id}/details`)}
                className="font-medium text-cyan-600 hover:text-cyan-800 hover:underline"
              >
                {apartment.code}
              </button>
            </div>
            <div>
              <span className="text-gray-600">Chủ hộ: </span>
              <span className="font-medium">{apartment.ownerName}</span>
            </div>
            <div>
              <span className="text-gray-600">Chung cư: </span>
              <span className="font-medium">{apartment.building.name}</span>
            </div>
            {apartment.ownerPhone && (
              <div>
                <span className="text-gray-600">Số điện thoại: </span>
                <span className="font-medium">{apartment.ownerPhone}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {apartment && (
        <ApartmentServiceFeePaymentModal
          key={paymentKey}
          embedded
          isOpen
          apartmentId={apartment.id}
          apartmentCode={apartment.code}
          onClose={() => setApartment(null)}
        />
      )}
    </div>
  );
};
